import { useParams, Link } from 'react-router-dom';
import { mangas } from '@/mocks/mangas';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import SEOMeta from '@/components/feature/SEOMeta';

const siteUrl = 'https://fanqiemanhua.com';

export default function MangaDetailPage() {
  const { mangaId } = useParams<{ mangaId: string }>();
  const manga = mangas.find((m) => m.id === mangaId);

  if (!manga) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该漫画可能已被下架或链接有误</p>
            <Link to="/" className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedMangas = mangas
    .filter((m) => m.id !== manga.id && m.category === manga.category)
    .slice(0, 4);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - 番茄漫画`,
    description: manga.description,
    author: { '@type': 'Person', name: manga.author },
    dateModified: manga.updatedAt,
    image: manga.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '番茄漫画',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/manga/${manga.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: manga.category, item: `${siteUrl}/category/${manga.category === '日漫' ? 'riman' : manga.category === '韩漫' ? 'hanman' : 'guoman'}` },
      { '@type': 'ListItem', position: 3, name: manga.title },
    ],
  };

  return (
    <>
      <SEOMeta
        title={`${manga.title} - 番茄漫画 | ${manga.author} ${manga.category} ${manga.status}在线下拉式阅读`}
        description={`${manga.title} - ${manga.description.substring(0, 120)} 番茄漫画提供${manga.title}最新话追漫提醒，下拉式滚动在线阅读体验。`}
        keywords={`${manga.title},${manga.author},${manga.category},${manga.tags.join(',')},免费漫画,下拉式阅读,番茄漫画`}
        canonicalUrl={`${siteUrl}/manga/${manga.id}`}
        ogImage={manga.coverImage}
        ogType="article"
        schema={{ ...schema, ...breadcrumbSchema }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: manga.category, href: `/category/${manga.category === '日漫' ? 'riman' : manga.category === '韩漫' ? 'hanman' : 'guoman'}` },
              { label: manga.title },
            ]}
          />

          <article className="flex flex-col lg:flex-row gap-8 py-8">
            <div className="flex-shrink-0 w-full sm:w-[280px] mx-auto lg:mx-0">
              <div className="rounded-xl overflow-hidden bg-background-100 aspect-[3/4]">
                <img
                  src={manga.coverImage}
                  alt={`${manga.title}封面`}
                  title={`${manga.title} - ${manga.author} | 番茄漫画`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {manga.isNew && (
                  <span className="px-2.5 py-0.5 text-xs font-semibold bg-primary-500 text-background-50 rounded-md whitespace-nowrap">NEW</span>
                )}
                <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-md whitespace-nowrap ${
                  manga.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-foreground-900/10 text-foreground-700'
                }`}>
                  {manga.status}
                </span>
                <span className="px-2.5 py-0.5 text-xs bg-secondary-100 text-secondary-700 rounded-md whitespace-nowrap">
                  {manga.category}
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                {manga.title}
              </h1>
              <p className="mt-1 text-sm text-foreground-500">作者：{manga.author}</p>

              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
                <span className="flex items-center gap-1 text-foreground-700">
                  <i className="ri-star-fill text-accent-500"></i>
                  <strong>{manga.rating}</strong> 评分
                </span>
                <span className="text-foreground-400">|</span>
                <span className="text-foreground-700">
                  <strong>{manga.views >= 10000 ? `${(manga.views / 10000).toFixed(0)}万` : manga.views}</strong> 阅读
                </span>
                <span className="text-foreground-400">|</span>
                <span className="text-foreground-600">
                  更新于 {manga.updatedAt}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {manga.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-6 p-5 rounded-xl bg-background-100">
                <h2 className="text-sm font-semibold text-foreground-950 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{manga.description}</p>
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-foreground-950">章节列表</h2>
                  <span className="text-xs text-foreground-500">共 {manga.chapters.length} 话</span>
                </div>
                <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto pr-2">
                  {manga.chapters.map((ch) => (
                    <div
                      key={ch.id}
                      className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
                    >
                      <span className="text-sm font-medium text-foreground-800">{ch.title}</span>
                      <span className="text-xs text-foreground-500 whitespace-nowrap">{ch.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {relatedMangas.length > 0 && (
            <section className="pb-14">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  相关推荐
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-product-shop="">
                {relatedMangas.map((m) => (
                  <MangaCard key={m.id} manga={m} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}